import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Lock, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FranchiseLogin = () => {
  const navigate = useNavigate();
  const [franchises, setFranchises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch franchises on component mount
  useEffect(() => {
    const fetchFranchises = async () => {
      try {
        const response = await axios.get(`${API}/franchises`);
        setFranchises(response.data);
      } catch (error) {
        console.error('Failed to load franchises:', error);
        toast.error('Failed to load franchise list');
      }
    };
    fetchFranchises();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('#franchise') && !event.target.closest('[data-dropdown]')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter franchises based on search term (show all if empty)
  const filteredFranchises = searchTerm 
    ? franchises.filter(franchise =>
        franchise.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : franchises;

  const handleFranchiseSelect = (franchise) => {
    setSelectedFranchise(franchise);
    setSearchTerm(franchise.name);
    setShowDropdown(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!selectedFranchise || !pin) {
      toast.error('Please select franchise and enter PIN');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/franchise/login`, {
        franchise_id: selectedFranchise.id,
        pin: pin,
      });
      
      if (response.data.success) {
        // Store franchise info in sessionStorage
        sessionStorage.setItem('franchise', JSON.stringify(response.data.franchise));
        toast.success('Login successful!');
        navigate('/order');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginCard}>
        <button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          style={styles.backButton}
        >
          <ArrowLeft size={20} />
        </button>
        
        <img src="/assets/logo.png" alt="Prakash Coffee Works" style={styles.logo} />
        <h2 style={styles.loginTitle}>Franchise Login</h2>
        <p style={styles.subtitle}>Enter your credentials to place an order</p>
        
        <form onSubmit={handleLogin} style={styles.loginForm}>
          <div style={styles.formGroup}>
            <label className="label" htmlFor="franchise">Select Franchise</label>
            <div style={styles.dropdownContainer}>
              <div style={styles.searchInputWrapper}>
                <input
                  id="franchise"
                  data-testid="franchise-search-input"
                  type="text"
                  className="input-field"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                    if (!e.target.value) {
                      setSelectedFranchise(null);
                    }
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Type to search franchise..."
                  required
                  autoComplete="off"
                  style={styles.searchInput}
                />
                <ChevronDown size={20} style={styles.dropdownIcon} />
              </div>
              
              {showDropdown && (
                <div style={styles.dropdownList} data-dropdown>
                  {filteredFranchises.length > 0 ? (
                    filteredFranchises.map((franchise) => (
                      <div
                        key={franchise.id}
                        style={styles.dropdownItem}
                        onClick={() => handleFranchiseSelect(franchise)}
                        onMouseEnter={(e) => e.target.style.background = '#f9f6f1'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                      >
                        <div style={styles.franchiseName}>{franchise.name}</div>
                        <div style={styles.franchiseLocation}>{franchise.location}</div>
                      </div>
                    ))
                  ) : (
                    <div style={styles.noResults}>No franchises found</div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div style={styles.formGroup}>
            <label className="label" htmlFor="pin">PIN</label>
            <input
              id="pin"
              data-testid="pin-input"
              type="password"
              className="input-field"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter your PIN"
              maxLength="5"
              required
            />
          </div>
          
          <button
            type="submit"
            data-testid="login-submit-btn"
            className="btn-primary"
            disabled={loading}
            style={{ width: '100%', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? (
              <><span className="spinner" />Logging in...</>
            ) : (
              <><Lock size={18} style={{ marginRight: '8px' }} />Login</>
            )}
          </button>
        </form>
        
        <div style={styles.helpText}>
          <p style={styles.helpTitle}>Need help?</p>
          <p style={styles.helpContent}>
            Contact head office if you don't have your PIN.
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  loginCard: {
    maxWidth: '450px',
    width: '100%',
    background: 'white',
    borderRadius: '16px',
    padding: 'clamp(24px, 5vw, 48px) clamp(20px, 4vw, 32px)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    textAlign: 'center',
    position: 'relative',
  },
  '@media (max-width: 768px)': {
    loginCard: {
      margin: '20px',
      borderRadius: '12px',
    },
  },
  backButton: {
    position: 'absolute',
    top: '16px',
    left: '16px',
    background: '#f5f0e8',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: '120px',
    height: '120px',
    objectFit: 'contain',
    margin: '0 auto 16px',
  },
  loginTitle: {
    fontSize: '28px',
    color: '#6b4423',
    margin: '16px 0 8px',
    fontFamily: "'League Spartan', sans-serif",
    fontWeight: '700',
  },
  subtitle: {
    fontSize: '14px',
    color: '#8b5e34',
    marginBottom: '32px',
  },
  loginForm: {
    marginTop: '24px',
  },
  formGroup: {
    marginBottom: '20px',
    textAlign: 'left',
    position: 'relative',
  },
  dropdownContainer: {
    position: 'relative',
  },
  searchInputWrapper: {
    position: 'relative',
  },
  searchInput: {
    paddingRight: '40px',
  },
  dropdownIcon: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#8b5e34',
    pointerEvents: 'none',
  },
  dropdownList: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    maxHeight: '250px',
    overflowY: 'auto',
    background: 'white',
    border: '1px solid #e8dcc8',
    borderRadius: '8px',
    marginTop: '4px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    zIndex: 1000,
  },
  dropdownItem: {
    padding: '12px 16px',
    cursor: 'pointer',
    borderBottom: '1px solid #f9f6f1',
    transition: 'background 0.2s',
  },
  franchiseName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#6b4423',
    marginBottom: '4px',
    fontFamily: "'League Spartan', sans-serif",
  },
  franchiseLocation: {
    fontSize: '12px',
    color: '#8b5e34',
  },
  noResults: {
    padding: '16px',
    textAlign: 'center',
    color: '#8b5e34',
    fontSize: '14px',
  },
  helpText: {
    marginTop: '24px',
    padding: '16px',
    background: '#f9f6f1',
    borderRadius: '8px',
    textAlign: 'left',
  },
  helpTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#6b4423',
    marginBottom: '4px',
  },
  helpContent: {
    fontSize: '13px',
    color: '#6b5d54',
    lineHeight: '1.4',
  },
};

export default FranchiseLogin;
